import React from 'react';
import { theme } from '../styles/theme';

// =========================================================================
// TYPES & PROP INTERFACES
// =========================================================================

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  options: { value: string; label: string }[];
  error?: string;
}

export interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
}

export interface RadioProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
}

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
}

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  hoverable?: boolean;
  elevation?: 'none' | 'xs' | 'sm' | 'md' | 'lg';
}

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: 'success' | 'warning' | 'error' | 'info' | 'primary';
}

export interface AvatarProps {
  src?: string;
  name: string;
  size?: 'sm' | 'md' | 'lg';
}

export interface DividerProps {
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  position?: 'left' | 'right';
  title: string;
  children: React.ReactNode;
}

export interface DropdownProps {
  label: string;
  items: { label: string; onClick: () => void }[];
}

export interface TooltipProps {
  text: string;
  children: React.ReactNode;
}

export interface LoaderProps {
  size?: 'sm' | 'md' | 'lg';
  color?: string;
}

export interface SkeletonProps {
  variant?: 'text' | 'rect' | 'circle';
  width?: string | number;
  height?: string | number;
}

export interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  onClose?: () => void;
}

export interface AlertProps {
  title?: string;
  message: string;
  type?: 'success' | 'warning' | 'error' | 'info';
}

export interface EmptyStateProps {
  icon: string;
  title: string;
  subtitle: string;
  actionText?: string;
  onActionClick?: () => void;
}

export interface LogoProps {
  theme?: 'light' | 'dark';
  size?: 'sm' | 'md' | 'lg';
  style?: React.CSSProperties;
}

export interface IconProps {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  color?: string;
}

// =========================================================================
// REUSABLE DESIGN SYSTEM COMPONENTS
// =========================================================================

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  style,
  ...props
}) => {
  const btnStyle = theme.buttons.variants[variant];
  const szStyle = theme.buttons.sizes[size];
  
  return (
    <button
      style={{
        backgroundColor: btnStyle.background,
        color: btnStyle.color,
        border: btnStyle.border,
        padding: szStyle.padding,
        fontSize: szStyle.fontSize,
        height: szStyle.height,
        borderRadius: theme.buttons.borderRadius,
        fontFamily: theme.typography.fontFamily.sans,
        fontWeight: theme.typography.fontWeight.semibold,
        width: fullWidth ? '100%' : 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        opacity: props.disabled ? 0.5 : 1,
        transition: theme.transitions.colors,
        minWidth: '48px', // Complies with accessibility touch target
        boxSizing: 'border-box',
        ...style
      }}
      {...props}
    >
      {children}
    </button>
  );
};

export const Input: React.FC<InputProps> = ({ label, error, style, ...props }) => {
  return (
    <div style={theme.inputs.container}>
      {label && <label style={theme.inputs.label}>{label}</label>}
      <input
        style={{
          ...theme.inputs.base,
          borderColor: error ? theme.colors.errorText : theme.colors.border,
          ...style
        }}
        {...props}
      />
      {error && <span style={theme.inputs.errorText}>{error}</span>}
    </div>
  );
};

export const TextArea: React.FC<TextAreaProps> = ({ label, error, style, ...props }) => {
  return (
    <div style={theme.inputs.container}>
      {label && <label style={theme.inputs.label}>{label}</label>}
      <textarea
        style={{
          ...theme.inputs.base,
          minHeight: '100px',
          borderColor: error ? theme.colors.errorText : theme.colors.border,
          ...style
        }}
        {...props}
      />
      {error && <span style={theme.inputs.errorText}>{error}</span>}
    </div>
  );
};

export const Select: React.FC<SelectProps> = ({ label, options, error, style, ...props }) => {
  return (
    <div style={theme.inputs.container}>
      {label && <label style={theme.inputs.label}>{label}</label>}
      <select
        style={{
          ...theme.inputs.base,
          borderColor: error ? theme.colors.errorText : theme.colors.border,
          ...style
        }}
        {...props}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && <span style={theme.inputs.errorText}>{error}</span>}
    </div>
  );
};

export const Checkbox: React.FC<CheckboxProps> = ({ label, style, ...props }) => {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, cursor: 'pointer', minHeight: '48px' }}>
      <input type="checkbox" style={{ width: '18px', height: '18px', cursor: 'pointer', ...style }} {...props} />
      <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textPrimary }}>{label}</span>
    </label>
  );
};

export const Radio: React.FC<RadioProps> = ({ label, style, ...props }) => {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, cursor: 'pointer', minHeight: '48px' }}>
      <input type="radio" style={{ width: '18px', height: '18px', cursor: 'pointer', ...style }} {...props} />
      <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textPrimary }}>{label}</span>
    </label>
  );
};

export const Switch: React.FC<SwitchProps> = ({ checked, onChange, label, disabled }) => {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, cursor: disabled ? 'not-allowed' : 'pointer', minHeight: '48px' }}>
      <div 
        onClick={() => !disabled && onChange(!checked)}
        style={{
          width: '44px',
          height: '24px',
          borderRadius: theme.radius.pill,
          backgroundColor: checked ? theme.colors.accent : theme.colors.border,
          position: 'relative',
          transition: theme.transitions.colors,
          opacity: disabled ? 0.5 : 1,
        }}
      >
        <div 
          style={{
            width: '18px',
            height: '18px',
            borderRadius: theme.radius.circle,
            backgroundColor: '#FFFFFF',
            position: 'absolute',
            top: '3px',
            left: checked ? '22px' : '3px',
            transition: 'left 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
            boxShadow: theme.shadows.xs,
          }}
        />
      </div>
      {label && <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.textPrimary }}>{label}</span>}
    </label>
  );
};

export const Card: React.FC<CardProps> = ({ children, hoverable = true, elevation = 'sm', style, ...props }) => {
  return (
    <div
      style={{
        backgroundColor: theme.colors.surface,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.radius.lg,
        padding: theme.spacing.md,
        boxShadow: theme.shadows[elevation],
        transition: theme.transitions.all,
        cursor: hoverable ? 'pointer' : 'default',
        transform: hoverable ? 'none' : undefined,
        ...style
      }}
      {...props}
    >
      {children}
    </div>
  );
};

export const Badge: React.FC<BadgeProps> = ({ children, variant = 'primary', style, ...props }) => {
  let bg = theme.colors.primaryLight;
  let text = theme.colors.primary;
  
  if (variant === 'success') { bg = theme.colors.success; text = theme.colors.successText; }
  else if (variant === 'warning') { bg = theme.colors.warning; text = theme.colors.warningText; }
  else if (variant === 'error') { bg = theme.colors.error; text = theme.colors.errorText; }
  else if (variant === 'info') { bg = theme.colors.info; text = theme.colors.infoText; }

  return (
    <span
      style={{
        backgroundColor: bg,
        color: text,
        padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
        borderRadius: theme.radius.pill,
        fontSize: '11px',
        fontWeight: theme.typography.fontWeight.bold,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        ...style
      }}
      {...props}
    >
      {children}
    </span>
  );
};

export const Avatar: React.FC<AvatarProps> = ({ src, name, size = 'md' }) => {
  let sizePx = '40px';
  if (size === 'sm') sizePx = '32px';
  if (size === 'lg') sizePx = '56px';

  const initial = name ? name.charAt(0).toUpperCase() : '?';

  return (
    <div
      style={{
        width: sizePx,
        height: sizePx,
        borderRadius: theme.radius.circle,
        backgroundColor: theme.colors.primaryLight,
        color: theme.colors.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: theme.typography.fontWeight.bold,
        fontSize: size === 'sm' ? '12px' : size === 'lg' ? '20px' : '15px',
        overflow: 'hidden',
        border: `2px solid ${theme.colors.border}`,
      }}
    >
      {src ? <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initial}
    </div>
  );
};

export const Divider: React.FC<DividerProps> = ({ orientation = 'horizontal', className }) => {
  return (
    <div 
      style={{
        backgroundColor: theme.colors.border,
        width: orientation === 'horizontal' ? '100%' : '1px',
        height: orientation === 'horizontal' ? '1px' : '100%',
        margin: orientation === 'horizontal' ? `${theme.spacing.md} 0` : `0 ${theme.spacing.md}`,
      }}
    />
  );
};

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: theme.zIndex.modal }}>
      <div style={{ backgroundColor: theme.colors.surface, borderRadius: theme.radius.lg, width: '90%', maxWidth: '500px', padding: theme.spacing.lg, boxShadow: theme.shadows.lg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.md }}>
          <h3 style={{ margin: 0, fontSize: theme.typography.fontSize.lg, color: theme.colors.textPrimary }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: theme.colors.textSecondary }}>&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const Drawer: React.FC<DrawerProps> = ({ isOpen, onClose, position = 'left', title, children }) => {
  if (!isOpen) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: theme.zIndex.drawer }}>
      <div 
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: position === 'left' ? 0 : 'auto',
          right: position === 'right' ? 0 : 'auto',
          width: '300px',
          backgroundColor: theme.colors.surface,
          padding: theme.spacing.lg,
          boxShadow: theme.shadows.lg,
          boxSizing: 'border-box'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.lg }}>
          <h3 style={{ margin: 0, fontSize: theme.typography.fontSize.lg }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}>&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
};

export const Dropdown: React.FC<DropdownProps> = ({ label, items }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <Button onClick={() => setOpen(!open)}>{label}</Button>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.md, boxShadow: theme.shadows.md, zIndex: theme.zIndex.dropdown, minWidth: '160px', marginTop: theme.spacing.xs }}>
          {items.map((item, index) => (
            <div 
              key={index} 
              onClick={() => { item.onClick(); setOpen(false); }}
              style={{ padding: `${theme.spacing.sm} ${theme.spacing.md}`, cursor: 'pointer', fontSize: theme.typography.fontSize.sm, color: theme.colors.textPrimary }}
            >
              {item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Tooltip: React.FC<TooltipProps> = ({ text, children }) => {
  const [visible, setVisible] = React.useState(false);
  return (
    <div 
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {children}
      {visible && (
        <div 
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: theme.colors.primary,
            color: '#FFFFFF',
            padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
            borderRadius: theme.radius.xs,
            fontSize: theme.typography.fontSize.xs,
            whiteSpace: 'nowrap',
            marginBottom: theme.spacing.xs,
            zIndex: theme.zIndex.tooltip
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
};

export const Loader: React.FC<LoaderProps> = ({ size = 'md', color = theme.colors.primary }) => {
  let dim = '24px';
  if (size === 'sm') dim = '16px';
  if (size === 'lg') dim = '48px';
  
  return (
    <div 
      style={{
        width: dim,
        height: dim,
        border: `3px solid ${theme.colors.border}`,
        borderTop: `3px solid ${color}`,
        borderRadius: theme.radius.circle,
        animation: 'spin 1s linear infinite'
      }}
    />
  );
};

export const Skeleton: React.FC<SkeletonProps> = ({ variant = 'rect', width = '100%', height = '16px' }) => {
  return (
    <div 
      style={{
        backgroundColor: theme.colors.surfaceMuted,
        width: width,
        height: height,
        borderRadius: variant === 'circle' ? theme.radius.circle : variant === 'text' ? theme.radius.xs : theme.radius.md,
        animation: 'pulse 1.5s ease-in-out infinite'
      }}
    />
  );
};

export const Toast: React.FC<ToastProps> = ({ message, type = 'success', onClose }) => {
  let bg = theme.colors.primary;
  if (type === 'error') bg = theme.colors.errorText;
  
  return (
    <div 
      style={{
        position: 'fixed',
        bottom: theme.spacing.lg,
        right: theme.spacing.lg,
        backgroundColor: bg,
        color: '#FFFFFF',
        padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
        borderRadius: theme.radius.md,
        boxShadow: theme.shadows.md,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.md,
        zIndex: theme.zIndex.toast
      }}
    >
      <span style={{ fontSize: theme.typography.fontSize.sm }}>{message}</span>
      {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#FFFFFF', cursor: 'pointer', fontSize: '16px' }}>&times;</button>}
    </div>
  );
};

export const Alert: React.FC<AlertProps> = ({ title, message, type = 'info' }) => {
  let bg = theme.colors.info;
  let text = theme.colors.infoText;
  let border = theme.colors.border;
  
  if (type === 'success') { bg = theme.colors.success; text = theme.colors.successText; border = theme.colors.successBorder; }
  else if (type === 'warning') { bg = theme.colors.warning; text = theme.colors.warningText; border = theme.colors.warning; }
  else if (type === 'error') { bg = theme.colors.error; text = theme.colors.errorText; border = theme.colors.errorText; }

  return (
    <div 
      style={{
        backgroundColor: bg,
        color: text,
        border: `1px solid ${border}`,
        borderRadius: theme.radius.md,
        padding: theme.spacing.md,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing.xs
      }}
    >
      {title && <h4 style={{ margin: 0, fontWeight: theme.typography.fontWeight.bold }}>{title}</h4>}
      <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm }}>{message}</p>
    </div>
  );
};

export const EmptyState: React.FC<EmptyStateProps> = ({ icon, title, subtitle, actionText, onActionClick }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: theme.spacing.xxl, textAlign: 'center', gap: theme.spacing.sm }}>
      <div style={{ fontSize: '48px', marginBottom: theme.spacing.sm }}>{icon}</div>
      <h3 style={{ margin: 0, fontSize: theme.typography.fontSize.lg, color: theme.colors.textPrimary }}>{title}</h3>
      <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary, maxWidth: '400px' }}>{subtitle}</p>
      {actionText && onActionClick && (
        <Button onClick={onActionClick} style={{ marginTop: theme.spacing.md }}>{actionText}</Button>
      )}
    </div>
  );
};

export const Logo: React.FC<LogoProps> = ({ theme: logoTheme = 'light', size = 'md', style }) => {
  const isDark = logoTheme === 'dark';
  const width = size === 'sm' ? '120px' : size === 'lg' ? '240px' : '180px';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, ...style }}>
      <div style={{ width: size === 'sm' ? '32px' : '40px', height: size === 'sm' ? '32px' : '40px', backgroundColor: isDark ? '#FFFFFF' : theme.colors.primary, borderRadius: theme.radius.md, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: isDark ? theme.colors.primary : '#FFFFFF', fontWeight: theme.typography.fontWeight.bold, fontSize: '20px' }}>M</span>
      </div>
      <span style={{ fontSize: size === 'sm' ? '16px' : '20px', fontWeight: theme.typography.fontWeight.bold, color: isDark ? '#FFFFFF' : theme.colors.primary, letterSpacing: '0.02em' }}>
        Millish Coaching OS
      </span>
    </div>
  );
};

export const Icon: React.FC<IconProps> = ({ name, size = 'md', color = theme.colors.textPrimary }) => {
  let dim = size === 'sm' ? '16px' : size === 'lg' ? '32px' : '24px';
  return (
    <span style={{ width: dim, height: dim, display: 'inline-block', fontSize: dim, lineHeight: '1', verticalAlign: 'middle', color: color }}>
      {name}
    </span>
  );
};
